import { useState, useEffect, useMemo } from 'react';
import Papa from 'papaparse';
import { Download } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Switch } from '@/components/ui/Switch';
import { Dropzone } from '@/components/ui/Dropzone';
import { Badge } from '@/components/ui/Badge';
import { Textarea } from '@/components/ui/Input';
import { useApp } from '@/store/app';
import { saveAs } from 'file-saver';
import { toast } from 'sonner';
import { readFileAsText } from '@/lib/utils';

const SAMPLE = `name, email,phone , city
Aarav Sharma  ,  hi@example.com, +91 999 ,Mumbai
Priya Mehta,priya@example.com,, Bengaluru
,,,
Aarav Sharma,hi@example.com,+91 999,Mumbai
Rohan Iyer , rohan@example.com ,+91 888,Pune  `;

export function CsvClean() {
  const { pendingFile, setPendingFile } = useApp();
  const [raw, setRaw] = useState(SAMPLE);
  const [opts, setOpts] = useState({
    trim: true,
    dedupe: true,
    removeEmpty: true,
    collapseSpaces: true,
    lowerHeaders: true,
  });

  useEffect(() => {
    if (pendingFile && (pendingFile.type === 'text/csv' || pendingFile.name.endsWith('.csv'))) {
      readFileAsText(pendingFile).then(setRaw);
      setPendingFile(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const parsed = useMemo(() => {
    if (!raw.trim()) return { headers: [], rows: [] as string[][], err: null as string | null };
    const r = Papa.parse<string[]>(raw, { skipEmptyLines: true });
    if (r.errors.length) return { headers: [], rows: [], err: r.errors[0].message };
    const all = r.data;
    const [h, ...rest] = all;
    return { headers: h ?? [], rows: rest, err: null };
  }, [raw]);

  const cleaned = useMemo(() => {
    let { headers, rows } = parsed;
    if (opts.trim) {
      headers = headers.map((h) => h.trim());
      rows = rows.map((r) => r.map((c) => c.trim()));
    }
    if (opts.collapseSpaces) {
      rows = rows.map((r) => r.map((c) => c.replace(/\s+/g, ' ')));
    }
    if (opts.lowerHeaders) headers = headers.map((h) => h.toLowerCase());
    if (opts.removeEmpty) {
      rows = rows.filter((r) => r.some((c) => c && c.length > 0));
    }
    if (opts.dedupe) {
      const seen = new Set<string>();
      rows = rows.filter((r) => {
        const key = r.join('||');
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    }
    return { headers, rows };
  }, [parsed, opts]);

  const stats = {
    inputRows: parsed.rows.length,
    outputRows: cleaned.rows.length,
    removed: parsed.rows.length - cleaned.rows.length,
  };

  function exportCsv() {
    const csv = Papa.unparse([cleaned.headers, ...cleaned.rows]);
    saveAs(new Blob([csv], { type: 'text/csv;charset=utf-8' }), 'cleaned.csv');
    toast.success('Saved');
  }

  return (
    <div>
      <ToolHeader
        toolId="csv-clean"
        title="CSV cleaner"
        description="Trim whitespace, normalize headers, drop blank rows, and remove duplicates."
        badges={parsed.err ? <Badge variant="danger">parse error</Badge> : <Badge variant="success">{stats.outputRows} rows</Badge>}
      />

      {!parsed.headers.length && !raw && (
        <Dropzone
          accept=".csv,text/csv"
          onFiles={async (f) => {
            const t = await readFileAsText(f[0]);
            setRaw(t);
          }}
          hint="CSV only"
        />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:order-2 space-y-2.5">
          <div className="text-xs font-medium text-fg-muted mb-1">Operations</div>
          <Toggle label="Trim cells" on={opts.trim} onChange={(v) => setOpts({ ...opts, trim: v })} />
          <Toggle label="Collapse spaces" on={opts.collapseSpaces} onChange={(v) => setOpts({ ...opts, collapseSpaces: v })} />
          <Toggle label="Remove empty rows" on={opts.removeEmpty} onChange={(v) => setOpts({ ...opts, removeEmpty: v })} />
          <Toggle label="Remove duplicates" on={opts.dedupe} onChange={(v) => setOpts({ ...opts, dedupe: v })} />
          <Toggle label="Lowercase headers" on={opts.lowerHeaders} onChange={(v) => setOpts({ ...opts, lowerHeaders: v })} />
          <div className="border-t border-border pt-3 mt-3 space-y-1.5 text-xs font-mono">
            <Stat label="Input rows" value={stats.inputRows} />
            <Stat label="Output rows" value={stats.outputRows} />
            <Stat label="Removed" value={stats.removed} highlight={stats.removed > 0} />
          </div>
          <Button variant="primary" className="w-full mt-2" onClick={exportCsv}>
            <Download size={12} />
            Export CSV
          </Button>
        </Card>

        <div className="lg:col-span-2 lg:order-1 space-y-3">
          <Card className="!p-0">
            <div className="px-4 py-2.5 border-b border-border text-xs font-medium text-fg-muted">
              Source
            </div>
            <Textarea
              mono
              value={raw}
              onChange={(e) => setRaw(e.target.value)}
              className="!border-0 !rounded-t-none min-h-[140px] !bg-transparent focus-visible:!ring-0"
              placeholder="Paste CSV content…"
            />
          </Card>

          {cleaned.headers.length > 0 && (
            <Card className="!p-0 overflow-hidden">
              <div className="px-4 py-2.5 border-b border-border text-xs font-medium text-fg-muted">
                Cleaned preview
              </div>
              <div className="overflow-auto max-h-[400px]">
                <table className="w-full text-sm">
                  <thead className="bg-bg-inset sticky top-0">
                    <tr>
                      {cleaned.headers.map((h, i) => (
                        <th
                          key={i}
                          className="text-left px-3 py-2 text-xs font-mono font-semibold text-fg-muted border-b border-border"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {cleaned.rows.slice(0, 200).map((row, i) => (
                      <tr key={i} className="hover:bg-surface-hover transition-colors">
                        {row.map((cell, j) => (
                          <td key={j} className="px-3 py-2 border-b border-border whitespace-nowrap">
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

function Toggle({ label, on, onChange }: { label: string; on: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center justify-between text-sm cursor-pointer">
      <span className="text-fg-muted">{label}</span>
      <Switch checked={on} onChange={onChange} />
    </label>
  );
}

function Stat({ label, value, highlight }: { label: string; value: number; highlight?: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-fg-subtle">{label}</span>
      <span className={highlight ? 'text-warning font-semibold' : ''}>{value}</span>
    </div>
  );
}

export default CsvClean;
